import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatErr, fmtGBP } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ImportCsv } from "@/components/ImportCsv";
import { Plus, Pencil, Trash, FloppyDisk, X, Wrench, Tag } from "@phosphor-icons/react";

const blankItem = { name: "", description: "", category_id: "", default_priority: "normal", default_sla_hours: 48, default_approval_required: true, default_estimated_cost: 0, preferred_skill_id: "" };
const REPAIR_ITEMS_INVALIDATE = ["repair-items", "repair-categories"];

export default function RepairItems() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: items = [] } = useQuery({ queryKey: ["repair-items"], queryFn: () => http.get("/repair-items").then(r => r.data) });
  const { data: cats = [] } = useQuery({ queryKey: ["repair-categories"], queryFn: () => http.get("/repair-categories").then(r => r.data) });
  const { data: skills = [] } = useQuery({ queryKey: ["skills"], queryFn: () => http.get("/skills").then(r => r.data) });

  const cMap = Object.fromEntries(cats.map(c => [c.id, c.name]));
  const sMap = Object.fromEntries(skills.map(s => [s.id, s.name]));

  const canManage = ["admin", "general_manager", "maintenance_manager"].includes(user?.role);
  const canDelete = ["admin", "maintenance_manager"].includes(user?.role);

  const [tab, setTab] = useState("items"); // 'items' | 'categories'
  const [itemModal, setItemModal] = useState(null);
  const [itemForm, setItemForm] = useState(blankItem);
  const [catModal, setCatModal] = useState(null);
  const [catForm, setCatForm] = useState({ name: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const openItem = (mode, it = blankItem) => {
    setItemForm({ ...blankItem, ...it, preferred_skill_id: it.preferred_skill_id || "" });
    setItemModal({ mode, id: it.id }); setErr("");
  };
  const submitItem = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const body = { ...itemForm, default_sla_hours: Number(itemForm.default_sla_hours), default_estimated_cost: Number(itemForm.default_estimated_cost) };
      if (itemModal.mode === "create") await http.post("/repair-items", body);
      else await http.patch(`/repair-items/${itemModal.id}`, body);
      qc.invalidateQueries({ queryKey: ["repair-items"] });
      setItemModal(null);
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const removeItem = async (id, name) => {
    if (!window.confirm(`Soft-delete repair item "${name}"?`)) return;
    try { await http.delete(`/repair-items/${id}`); qc.invalidateQueries({ queryKey: ["repair-items"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  const submitCat = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (catModal.mode === "create") await http.post("/repair-categories", catForm);
      else await http.patch(`/repair-categories/${catModal.id}`, catForm);
      qc.invalidateQueries({ queryKey: ["repair-categories"] });
      setCatModal(null);
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const removeCat = async (id, name) => {
    if (!window.confirm(`Soft-delete category "${name}"?`)) return;
    try { await http.delete(`/repair-categories/${id}`); qc.invalidateQueries({ queryKey: ["repair-categories"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Building maintenance</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1">Repair items</h1>
          <p className="text-sm text-slate-600 mt-1">Templates for non-equipment repairs — plumbing, electrical, HVAC, fabric. Equipment-specific repairs are raised against an asset on the <a href="/equipment" className="text-[#002FA7] hover:underline">Equipment</a> page.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden">
            <button data-testid="tab-items" onClick={() => setTab("items")} className={`px-3 h-9 text-xs font-heading uppercase tracking-wider ${tab==="items"?"bg-slate-900 text-white":"bg-white hover:bg-slate-50"}`}>Items</button>
            <button data-testid="tab-categories" onClick={() => setTab("categories")} className={`px-3 h-9 text-xs font-heading uppercase tracking-wider ${tab==="categories"?"bg-slate-900 text-white":"bg-white hover:bg-slate-50"}`}>Categories</button>
          </div>
          {canManage && tab === "items" && (
            <ImportCsv resource="repair-items" invalidateKeys={REPAIR_ITEMS_INVALIDATE} />
          )}
          {canManage && tab === "items" && (
            <button onClick={() => openItem("create")} data-testid="item-new-button" className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
              <Plus size={14} weight="bold" /> Add item
            </button>
          )}
          {canManage && tab === "categories" && (
            <button onClick={() => { setCatForm({ name: "" }); setCatModal({ mode: "create" }); }} data-testid="category-new-button" className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
              <Plus size={14} weight="bold" /> Add category
            </button>
          )}
        </div>
      </div>

      {tab === "items" ? (
        <div className="rmms-card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] font-heading uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Category</th><th className="px-4 py-2.5">Priority</th><th className="px-4 py-2.5">SLA (h)</th><th className="px-4 py-2.5">Approval</th><th className="px-4 py-2.5 text-right">Default Cost</th><th className="px-4 py-2.5">Skill</th><th className="px-4 py-2.5 w-24"></th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {items.map(i => (
                <tr key={i.id} data-testid={`repair-item-row-${i.id}`}>
                  <td className="px-4 py-3 font-medium">{i.name}</td>
                  <td className="px-4 py-3">{cMap[i.category_id] || "—"}</td>
                  <td className="px-4 py-3 capitalize">{i.default_priority}</td>
                  <td className="px-4 py-3 font-mono-data">{i.default_sla_hours}</td>
                  <td className="px-4 py-3 text-xs">{i.default_approval_required ? "Required" : "Optional"}</td>
                  <td className="px-4 py-3 text-right font-mono-data">{fmtGBP(i.default_estimated_cost)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{sMap[i.preferred_skill_id] || i.preferred_skill_tag || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      {canManage && <button onClick={() => openItem("edit", i)} data-testid={`item-edit-${i.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={12} /></button>}
                      {canDelete && <button onClick={() => removeItem(i.id, i.name)} data-testid={`item-delete-${i.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={12} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rmms-card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] font-heading uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="px-4 py-2.5">Category</th><th className="px-4 py-2.5">Items</th><th className="px-4 py-2.5 w-24"></th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {cats.map(c => (
                <tr key={c.id} data-testid={`category-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium flex items-center gap-2"><Tag size={12} />{c.name}</td>
                  <td className="px-4 py-3 font-mono-data">{items.filter(i => i.category_id === c.id).length}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      {canManage && <button onClick={() => { setCatForm({ name: c.name }); setCatModal({ mode: "edit", id: c.id }); }} data-testid={`category-edit-${c.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={12} /></button>}
                      {canDelete && <button onClick={() => removeCat(c.id, c.name)} data-testid={`category-delete-${c.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={12} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {itemModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="item-modal">
          <form onSubmit={submitItem} className="bg-white border border-slate-200 rounded-md w-full max-w-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div><div className="overline">{itemModal.mode === "create" ? "New" : "Edit"}</div><h2 className="text-xl font-heading font-semibold tracking-tight mt-1">Repair item</h2></div>
              <button type="button" onClick={() => setItemModal(null)} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div>
              <label className="overline mb-1.5 block">Name *</label>
              <input required value={itemForm.name} onChange={e => setItemForm({ ...itemForm, name: e.target.value })} data-testid="item-name-input" className="rmms-input" />
            </div>
            <div>
              <label className="overline mb-1.5 block">Description</label>
              <textarea value={itemForm.description} onChange={e => setItemForm({ ...itemForm, description: e.target.value })} rows={2} data-testid="item-description-input" className="w-full rounded-md border border-slate-200 p-3 text-sm outline-none focus:border-[#002FA7]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline mb-1.5 block">Category *</label>
                <select required value={itemForm.category_id} onChange={e => setItemForm({ ...itemForm, category_id: e.target.value })} data-testid="item-category-select" className="rmms-input">
                  <option value="">Select…</option>
                  {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="overline mb-1.5 block">Preferred skill</label>
                <select value={itemForm.preferred_skill_id} onChange={e => setItemForm({ ...itemForm, preferred_skill_id: e.target.value })} data-testid="item-skill-select" className="rmms-input">
                  <option value="">(none)</option>
                  {skills.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="overline mb-1.5 block">Priority</label>
                <select value={itemForm.default_priority} onChange={e => setItemForm({ ...itemForm, default_priority: e.target.value })} data-testid="item-priority-select" className="rmms-input">
                  {["low", "normal", "high", "urgent"].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="overline mb-1.5 block">SLA (h)</label>
                <input type="number" min={1} value={itemForm.default_sla_hours} onChange={e => setItemForm({ ...itemForm, default_sla_hours: e.target.value })} data-testid="item-sla-input" className="rmms-input font-mono-data" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Default cost (£)</label>
                <input type="number" step="0.01" value={itemForm.default_estimated_cost} onChange={e => setItemForm({ ...itemForm, default_estimated_cost: e.target.value })} data-testid="item-cost-input" className="rmms-input font-mono-data" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={itemForm.default_approval_required} onChange={e => setItemForm({ ...itemForm, default_approval_required: e.target.checked })} data-testid="item-approval-checkbox" />
              Requires approval by default
            </label>
            {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button type="button" onClick={() => setItemModal(null)} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} data-testid="item-submit-button" className="h-10 px-4 rounded-md text-white text-sm inline-flex items-center gap-1.5" style={{ background: "#002FA7" }}>
                <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}

      {catModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="category-modal">
          <form onSubmit={submitCat} className="bg-white border border-slate-200 rounded-md w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div><div className="overline">{catModal.mode === "create" ? "New" : "Edit"}</div><h2 className="text-xl font-heading font-semibold tracking-tight mt-1">Category</h2></div>
              <button type="button" onClick={() => setCatModal(null)} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div>
              <label className="overline mb-1.5 block">Name *</label>
              <input required value={catForm.name} onChange={e => setCatForm({ name: e.target.value })} data-testid="category-name-input" className="rmms-input" />
            </div>
            {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button type="button" onClick={() => setCatModal(null)} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} data-testid="category-submit-button" className="h-10 px-4 rounded-md text-white text-sm inline-flex items-center gap-1.5" style={{ background: "#002FA7" }}>
                <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
